import sumBy from 'lodash/sumBy';
import React, { useMemo } from 'react';

import { QUERY_ROOT_ID, useCompleteFragment } from '@eluve/apollo-client';
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
  Icon,
  Stack,
  Text,
} from '@eluve/components';

import { artifactsFragment } from './eval.operations';

export type DataSetItem = {
  id: string;
  name: string;
  totalArtifacts: number;
  totalFacts: number;
};

export interface ArtifactSearchProps {
  selectedDataSetItems: DataSetItem[];
  onSelectionChanged: (selectedDataSetItems: DataSetItem[]) => void;
}

export const ArtifactSearch: React.FC<ArtifactSearchProps> = ({
  onSelectionChanged,
  selectedDataSetItems,
}) => {
  const { evalDatasets } = useCompleteFragment({
    fragment: artifactsFragment,
    key: QUERY_ROOT_ID,
  });

  const commandItems = useMemo(() => {
    return (evalDatasets ?? []).map<DataSetItem>((d) => ({
      id: d.id,
      name: d.name,
      totalArtifacts: d.datasetSourceArtifacts.length,
      totalFacts: sumBy(
        d.datasetSourceArtifacts,
        (a) => a.sourceArtifact?.factsAggregate?.aggregate?.count ?? 0,
      ),
    }));
  }, [evalDatasets]);

  return (
    <Command className="w-full border">
      <CommandInput placeholder="Search for a dataset(s) to include artifacts" />
      <CommandEmpty>No datasets found</CommandEmpty>
      <CommandList>
        {commandItems.map((item) => (
          <Stack key={item.id} direction="horizontal" align="center">
            <CommandItem
              key={item.id}
              className="flex-1"
              value={`${item.id}-${item.name}`}
              onSelect={() => {
                onSelectionChanged(
                  selectedDataSetItems.some((s) => s.id === item.id)
                    ? selectedDataSetItems.filter((s) => s.id !== item.id)
                    : [...selectedDataSetItems, item],
                );
              }}
            >
              <Stack direction="horizontal" align="center" justify="between">
                <Stack
                  direction="horizontal"
                  align="center"
                  gap={2}
                  fullWidth={false}
                >
                  {selectedDataSetItems.some((s) => s.id === item.id) ? (
                    <Icon name="Check" className="text-bg-lightgreen-strong" />
                  ) : (
                    <div className="size-4" />
                  )}
                  <Text>{item.name}</Text>
                </Stack>
                <Text>{`${item.totalArtifacts} artifact(s) with ${item.totalFacts} fact(s)`}</Text>
              </Stack>
            </CommandItem>
          </Stack>
        ))}
      </CommandList>
    </Command>
  );
};
